import React, { useEffect, useMemo } from 'react';

import { CurrentSession, useLazyGetCurrentSessionsWithNotificationQuery } from 'store/exams-search/examsSearchAPI';
import { selectSelectedAwardingBody, selectSelectedSession, setSession } from 'store/exams-search/examsSearchSlice';
import { useAppDispatch, useAppSelector } from 'store';
import DOMPurify from 'dompurify';

import { ApiErrorPage, BcSelect } from '@britishcouncil/react-solas-ors3';
import { Alert, LoadingContainer } from '@britishcouncil/react-solas';

const WARNING_TYPES: Array<'success' | 'info' | 'warning' | 'danger'> = ['success', 'info', 'warning', 'danger'];

export const SessionSelect = () => {
  const dispatch = useAppDispatch();
  const awardingBody = useAppSelector(selectSelectedAwardingBody);
  const session = useAppSelector(selectSelectedSession);
  const [triggerCurrentSessionsQuery, { data, error, isFetching }] = useLazyGetCurrentSessionsWithNotificationQuery();

  const selectedSession = useMemo(() => data?.find((x) => x.value === session?.sessionId), [session, data]);

  const sessionNotification = useMemo(
    () =>
      selectedSession && selectedSession.notificationInfo
        ? {
            ...selectedSession.notificationInfo,
            type: WARNING_TYPES.find((t) => t === selectedSession?.notificationInfo?.type) ?? 'info',
          }
        : undefined,
    [selectedSession]
  );

  useEffect(() => {
    if (awardingBody) {
      triggerCurrentSessionsQuery(awardingBody);
    }
  }, [triggerCurrentSessionsQuery, awardingBody]);

  if (!awardingBody) return null;

  if (error) return <ApiErrorPage />;

  return (
    <LoadingContainer loading={isFetching}>
      {data && data.length === 0 && <Alert type="warning">There are currently no active sessions</Alert>}
      {data && data.length > 0 && (
        <>
          <label htmlFor="session-select">Session</label>
          <BcSelect
            id="session-select"
            isLoading={isFetching}
            options={data}
            value={session?.sessionId}
            placeholder="Select session"
            onChange={(_selected: unknown, session: unknown) =>
              dispatch(
                setSession([
                  (session as CurrentSession).value,
                  (session as CurrentSession).checkpointOnly,
                  (session as CurrentSession).productSubSystemId,
                ])
              )
            }
          />
        </>
      )}
      {sessionNotification && (
        <Alert type={sessionNotification.type}>
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sessionNotification.content) }} />
        </Alert>
      )}
    </LoadingContainer>
  );
};
